import { useState } from "react";
import { RefreshCw, XCircle, ExternalLink, ArrowLeftRight, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { dateStyle: "long" }) : "—");

export function SubscriptionManager({ billing, onChanged }) {
  const sub = billing?.subscription;
  const [busy, setBusy] = useState("");
  if (!sub?.active) return null;
  const otherPlan = sub.plan === "annual" ? "monthly" : "annual";
  const otherPrice = otherPlan === "annual" ? billing.annual_price_usd : billing.monthly_price_usd;

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast.success(ok);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Request failed");
    } finally {
      setBusy("");
    }
  };

  const openPortal = () => run("portal", async () => {
    const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
    window.location.href = data.url;
  });
  const cancel = () => {
    if (!window.confirm(`Stop auto-renewal? You keep full access until ${fmt(billing.paid_until)}; nothing more will be charged.`)) return;
    run("cancel", () => api.post("/billing/subscription/cancel"), "Auto-renewal canceled.");
  };
  const resume = () => run("resume", () => api.post("/billing/subscription/resume"), "Auto-renewal resumed.");
  const switchPlan = () => {
    if (!window.confirm(`Switch to the ${otherPlan} plan at $${Number(otherPrice).toFixed(2)}? Stripe prorates the difference automatically.`)) return;
    run("plan", () => api.post("/billing/subscription/plan", { plan: otherPlan }), `Switched to ${otherPlan} billing.`);
  };

  const Btn = ({ k, onClick, children, tone = "" }) => (
    <button type="button" onClick={onClick} disabled={!!busy} data-testid={`subscription-${k}-btn`}
      className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-semibold transition disabled:opacity-60 ${tone || "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"}`}>
      {busy === k ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
    </button>
  );

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 mb-6" data-testid="subscription-manager">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Auto-renewal</div>
      <div className="font-display text-xl font-bold text-slate-900" data-testid="subscription-headline">
        {sub.cancel_at_period_end
          ? `Cancels on ${fmt(billing.paid_until)}`
          : sub.status === "past_due"
          ? "Payment problem — card declined"
          : `Auto-renews ${sub.plan === "annual" ? "yearly" : "monthly"} on ${fmt(sub.current_period_end || billing.paid_until)}`}
      </div>
      <p className="mt-1 text-sm text-slate-600">
        {sub.cancel_at_period_end
          ? "Your card will not be charged again. Access continues until the date above."
          : sub.status === "past_due"
          ? `Stripe is retrying your card. Update it below to keep access beyond ${fmt(billing.paid_until)}.`
          : `Your saved card is charged automatically every ${sub.plan === "annual" ? "year" : "30 days"} until you cancel.`}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        <Btn k="portal" onClick={openPortal}><ExternalLink className="h-4 w-4" /> Update card · invoices</Btn>
        {!sub.cancel_at_period_end && (
          <Btn k="plan" onClick={switchPlan}><ArrowLeftRight className="h-4 w-4" /> Switch to {otherPlan} · ${Number(otherPrice).toFixed(2)}</Btn>
        )}
        {sub.cancel_at_period_end
          ? <Btn k="resume" onClick={resume} tone="border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"><RefreshCw className="h-4 w-4" /> Resume auto-renewal</Btn>
          : <Btn k="cancel" onClick={cancel} tone="border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"><XCircle className="h-4 w-4" /> Cancel auto-renewal</Btn>}
      </div>
    </section>
  );
}
